import { useEffect, useMemo } from 'react';
import type { TagUntagUsersContext } from '@scale/genai-compass-shared/cards/tag_untag_users';
import { TAG_UNTAG_ACTION } from '@scale/genai-compass-shared/cards/tag_untag_users';
import { columnsFromRecord } from '@scale/compass-shared/utils';
import type { Column } from '@scale/compass-shared/types';

export default function useTagUntagUsersCard(
  context: TagUntagUsersContext,
  updateCardContext: (context: TagUntagUsersContext) => void,
  updateCardCanSubmit: (canSubmit: boolean) => void,
  previousCardData?: {
    columns: Column[];
    data: Record<string, any>[];
  },
) {
  type HeadersState = {
    userIdHeader?: string;
    tagIdHeader?: string;
    actionHeader?: string;
    error: string | null;
  };
  const {
    userIdHeader,
    tagIdHeader,
    actionHeader,
    error: columnError,
  } = useMemo<HeadersState>(() => {
    try {
      const columns = columnsFromRecord(previousCardData?.data);

      const userIdCol = columns.find(
        (col: Column) =>
          col.header.toLowerCase() === 'userid' || col.header.toLowerCase() === 'user_id',
      );

      const tagIdCol = columns.find(
        (col: Column) =>
          col.header.toLowerCase() === 'tagid' || col.header.toLowerCase() === 'tag_id',
      );

      const actionCol = columns.find((col: Column) => col.header.toLowerCase() === 'action');

      const result: HeadersState = {
        userIdHeader: userIdCol?.header,
        tagIdHeader: tagIdCol?.header,
        actionHeader: actionCol?.header,
        error: null,
      };

      if (!userIdCol) {
        result.error = `No userId column found. Please name your user ID column exactly 'userId' or 'user_id'.`;
        return result;
      }

      if (!tagIdCol) {
        result.error = `No tagId column found. Please name your tag ID column exactly 'tagId' or 'tag_id'.`;
        return result;
      }

      if (!actionCol) {
        result.error = `No action column found. Please name your action column exactly 'action'.`;
        return result;
      }

      const rows = previousCardData?.data;
      if (rows && Array.isArray(rows)) {
        const hasInvalidActions = rows.some(row => {
          const actionValue = row[actionCol.header]?.toString().toLowerCase();
          return (
            actionValue &&
            actionValue !== TAG_UNTAG_ACTION.TAG &&
            actionValue !== TAG_UNTAG_ACTION.UNTAG
          );
        });

        if (hasInvalidActions) {
          return {
            userIdHeader: userIdCol.header,
            tagIdHeader: tagIdCol.header,
            actionHeader: actionCol.header,
            error: `Invalid action values found. Action must be 'tag' or 'untag'.`,
          };
        }
      }

      return {
        userIdHeader: userIdCol.header,
        tagIdHeader: tagIdCol.header,
        actionHeader: actionCol.header,
        error: null,
      };
    } catch (error) {
      return {
        error: 'Error processing columns from prior card.',
      };
    }
  }, [previousCardData]);

  const canSubmit = useMemo(
    () => !!(userIdHeader && tagIdHeader && actionHeader && !columnError),
    [userIdHeader, tagIdHeader, actionHeader, columnError],
  );

  useEffect(() => {
    updateCardCanSubmit(canSubmit);
  }, [canSubmit, updateCardCanSubmit]);

  useEffect(() => {
    if (!canSubmit) return;

    const nextContext: TagUntagUsersContext = {
      ...context,
      userIdColumn: userIdHeader!,
      tagIdColumn: tagIdHeader!,
      actionColumn: actionHeader!,
    };

    if (
      nextContext.userIdColumn !== context.userIdColumn ||
      nextContext.tagIdColumn !== context.tagIdColumn ||
      nextContext.actionColumn !== context.actionColumn
    ) {
      updateCardContext(nextContext);
    }
  }, [
    canSubmit,
    userIdHeader,
    tagIdHeader,
    actionHeader,
    context,
    context.userIdColumn,
    context.tagIdColumn,
    context.actionColumn,
    updateCardContext,
  ]);

  return {
    userIdHeader,
    tagIdHeader,
    actionHeader,
    columnError,
  };
}
